import React, { useState, useEffect, useCallback } from 'react';
/* eslint-disable import/first */
import { Toaster } from 'sonner';
import axios from 'axios';
import Sidebar from './components/Sidebar';
import ModuleSelector from './pages/ModuleSelector';
import Dashboard from './pages/Dashboard';
import ProductionPlanning from './pages/ProductionPlanning';
import PlanningProjects from './pages/PlanningProjects';
import Personnel from './pages/Personnel';
import Projects from './pages/Projects';
import Shifts from './pages/Shifts';
import Reports from './pages/Reports';
import Backup from './pages/Backup';
import UserManagement from './pages/UserManagement';
import LineManagement from './pages/LineManagement';
import WarehouseHome from './pages/warehouse/WarehouseHome';
import PortalGate from './pages/spares/PortalGate';
import SparePartsPortal from './pages/spares/SparePartsPortal';
import SpareAdmin from './pages/spares/SpareAdmin';
import './App.css';

// Global axios auth interceptor — routes the correct token per API namespace.
// Spare Parts portal (/api/spares/*) is a fully isolated auth domain.
axios.interceptors.request.use((config) => {
  const url = config.url || '';
  if (url.includes('/api/spares/users')) {
    const t = localStorage.getItem('spare_admin_token');
    if (t) config.headers.Authorization = `Bearer ${t}`;
  } else if (url.includes('/api/spares/')) {
    const t = localStorage.getItem('spare_token');
    if (t) config.headers.Authorization = `Bearer ${t}`;
  } else {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
axios.interceptors.response.use(
  (res) => res,
  (err) => {
    const url = err.config?.url || '';
    if (err.response?.status === 401 && !url.includes('/auth/') && !url.includes('/api/spares/')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/';
    }
    return Promise.reject(err);
  }
);

const API = process.env.REACT_APP_BACKEND_URL;

function App() {
  const [activeModule, setActiveModule] = useState(() => localStorage.getItem('activeModule') || null);
  const [activePage, setActivePage] = useState(() => localStorage.getItem('activePage') || 'planning');

  useEffect(() => {
    if (activeModule) localStorage.setItem('activeModule', activeModule);
    else localStorage.removeItem('activeModule');
  }, [activeModule]);
  useEffect(() => { localStorage.setItem('activePage', activePage); }, [activePage]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [spareUser, setSpareUser] = useState(null);
  const [spareAdmin, setSpareAdmin] = useState(null);

  const [planningTargetDate, setPlanningTargetDate] = useState(null);

  const handleNavigateToPlanning = (date) => {
    setPlanningTargetDate(date);
    setActivePage('planning');
  };

  const verifyToken = useCallback(async () => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (token && storedUser) {
      try {
        const res = await fetch(`${API}/api/auth/me`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) { setUser(JSON.parse(storedUser)); }
        else { localStorage.removeItem('token'); localStorage.removeItem('user'); }
      } catch { /* offline - trust stored */ setUser(JSON.parse(storedUser)); }
      setAuthLoading(false);
      return;
    }
    // Spare Parts customer session
    const spareToken = localStorage.getItem('spare_token');
    if (spareToken) {
      try {
        const res = await fetch(`${API}/api/spares/auth/me`, { headers: { 'Authorization': `Bearer ${spareToken}` } });
        if (res.ok) { setSpareUser(await res.json()); }
        else { localStorage.removeItem('spare_token'); localStorage.removeItem('spare_user'); }
      } catch { /* ignore */ }
    }
    // Spare Parts admin session (management super_admin credentials, isolated storage)
    const adminToken = localStorage.getItem('spare_admin_token');
    const adminUser = localStorage.getItem('spare_admin_user');
    if (adminToken && adminUser) {
      try {
        const res = await fetch(`${API}/api/auth/me`, { headers: { 'Authorization': `Bearer ${adminToken}` } });
        if (res.ok) {
          const u = JSON.parse(adminUser);
          if (u.role === 'super_admin') setSpareAdmin(u);
        } else { localStorage.removeItem('spare_admin_token'); localStorage.removeItem('spare_admin_user'); }
      } catch { /* ignore */ }
    }
    setAuthLoading(false);
  }, []);

  useEffect(() => { verifyToken(); }, [verifyToken]);

  const handleLogin = (userData, token) => { setUser(userData); };
  const handleLogout = () => {
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${API}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      }).catch(() => {});
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('activeModule');
    localStorage.removeItem('activePage');
    setUser(null);
    setActiveModule(null);
  };

  const isAdmin = user?.role === 'super_admin' || user?.role === 'editor';
  const isSuperAdmin = user?.role === 'super_admin';

  const handleModuleSelect = (mod) => {
    setActiveModule(mod);
    setActivePage(mod === 'planning' ? 'planning' : 'dashboard');
  };

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a1628' }}>
      <div className="text-slate-400 text-sm">Loading...</div>
    </div>;
  }

  if (!user) {
    // Spare Parts customer session
    if (spareUser) {
      return (
        <>
          <Toaster position="top-right" toastOptions={{ style: { background: '#fff', border: '1px solid #E2E8F0', color: '#334155' } }} />
          <SparePartsPortal customer={spareUser}
            onLogout={() => {
              localStorage.removeItem('spare_token');
              localStorage.removeItem('spare_user');
              setSpareUser(null);
            }} />
        </>
      );
    }
    // Spare Parts admin session (customer account management)
    if (spareAdmin) {
      return (
        <>
          <Toaster position="top-right" toastOptions={{ style: { background: '#fff', border: '1px solid #E2E8F0', color: '#334155' } }} />
          <SpareAdmin admin={spareAdmin}
            onExit={() => {
              localStorage.removeItem('spare_admin_token');
              localStorage.removeItem('spare_admin_user');
              setSpareAdmin(null);
            }} />
        </>
      );
    }
    // Dual-entrance gate: customer portal (default) + employee/management + portal admin
    return (
      <>
        <Toaster position="top-right" toastOptions={{ style: { background: '#fff', border: '1px solid #E2E8F0', color: '#334155' } }} />
        <PortalGate
          onCustomerLogin={(c) => setSpareUser(c)}
          onEmployeeLogin={handleLogin}
          onAdminLogin={(u) => setSpareAdmin(u)} />
      </>
    );
  }

  // Module selector screen
  if (!activeModule) {
    return (
      <>
        <Toaster position="top-right" toastOptions={{ style: { background: '#fff', border: '1px solid #E2E8F0', color: '#334155' } }} />
        <ModuleSelector onSelectModule={handleModuleSelect} user={user} onLogout={handleLogout} />
      </>
    );
  }

  // Warehouse module — standalone, no sidebar
  if (activeModule === 'warehouse') {
    return (
      <>
        <Toaster position="top-right" toastOptions={{ style: { background: '#fff', border: '1px solid #E2E8F0', color: '#334155' } }} />
        <WarehouseHome onBack={() => setActiveModule(null)} />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-background industrial-bg">
      <Toaster position="top-right" toastOptions={{ style: { background: '#fff', border: '1px solid #E2E8F0', color: '#334155' } }} />
      
      <Sidebar 
        activeModule={activeModule}
        activePage={activePage}
        onPageChange={setActivePage}
        onBackToModules={() => setActiveModule(null)}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        user={user}
        onLogout={handleLogout}
      />
      
      <main className={`transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-60'}`}>
        <div className="p-6 max-w-[1920px] mx-auto">
          {/* Production Simulation pages */}
          {activeModule === 'production' && (
            <>
              <div style={{ display: activePage === 'dashboard' ? 'block' : 'none' }}>
                <Dashboard />
              </div>
              {activePage === 'projects' && <Projects />}
              {activePage === 'personnel' && <Personnel />}
              {activePage === 'shifts' && <Shifts />}
              {activePage === 'backup' && <Backup />}
            </>
          )}
          {/* Planning pages */}
          {activeModule === 'planning' && (
            <>
              {activePage === 'planning' && <ProductionPlanning isAdmin={isAdmin} targetDate={planningTargetDate} onTargetDateConsumed={() => setPlanningTargetDate(null)} />}
              {activePage === 'planning-projects' && <PlanningProjects isAdmin={isAdmin} />}
              {activePage === 'planning-reports' && <Reports onNavigateToPlanning={handleNavigateToPlanning} />}
              {activePage === 'backup' && <Backup />}
              {activePage === 'lines' && <LineManagement isAdmin={isAdmin} />}
              {activePage === 'users' && isSuperAdmin && <UserManagement />}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default App;
